import { Box, Button, Divider, Drawer, IconButton, Typography } from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import RemoveRoundedIcon from "@mui/icons-material/RemoveRounded";
import { useNavigate } from "react-router-dom";
import { useApp } from "../context/AuthContext";

export default function CartDrawer() {
  const navigate = useNavigate();
  const {
    cartItems,
    cartOpen,
    setCartOpen,
    removeFromCart,
    clearCart,
    getCartSubtotal,
    increaseCartItemQuantity,
    decreaseCartItemQuantity,
  } = useApp();

  const total = getCartSubtotal();

  const handleClose = () => {
    setCartOpen(false);
  };

  const handleGoCheckout = () => {
    setCartOpen(false);
    navigate("/checkout");
  };

  return (
    <Drawer
      anchor="right"
      open={cartOpen}
      onClose={handleClose}
      PaperProps={{
        sx: {
          width: { xs: "100%", sm: 390 },
          maxWidth: "100%",
          bgcolor: "#ffffff",
        },
      }}
    >
      <Box
        sx={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box
          sx={{
            px: 2,
            py: 1.8,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            borderBottom: "1px solid rgba(17,17,17,0.08)",
          }}
        >
          <Typography
            sx={{
              fontSize: 22,
              fontWeight: 800,
              color: "#111111",
            }}
          >
            Carrito
          </Typography>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {!!cartItems.length && (
              <Button
                onClick={clearCart}
                sx={{
                  minWidth: "auto",
                  px: 1.4,
                  py: 0.65,
                  borderRadius: 999,
                  textTransform: "none",
                  fontSize: 13.5,
                  fontWeight: 700,
                  color: "#111111",
                  border: "1px solid rgba(17,17,17,0.10)",
                }}
              >
                Limpiar
              </Button>
            )}

            <IconButton
              onClick={handleClose}
              aria-label="Cerrar carrito"
              sx={{
                width: 40,
                height: 40,
                borderRadius: 3,
                color: "#111111",
                border: "1px solid rgba(17,17,17,0.08)",
                bgcolor: "#ffffff",
                "&:hover": {
                  color: "#e50914",
                  bgcolor: "rgba(229,9,20,0.06)",
                  borderColor: "rgba(229,9,20,0.18)",
                },
              }}
            >
              <CloseRoundedIcon />
            </IconButton>
          </Box>
        </Box>

        {cartItems.length === 0 ? (
          <Box
            sx={{
              flex: 1,
              px: 3,
              py: 5,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              textAlign: "center",
            }}
          >
            <ShoppingCartOutlinedIcon sx={{ fontSize: 52, color: "rgba(0,0,0,0.25)" }} />

            <Typography
              sx={{
                mt: 2,
                fontSize: 24,
                fontWeight: 900,
                color: "#111111",
              }}
            >
              Tu carrito está vacío
            </Typography>

            <Typography
              sx={{
                mt: 1,
                fontSize: 14,
                color: "rgba(17,17,17,0.64)",
                maxWidth: 260,
              }}
            >
              Añade productos desde la tienda para verlos aquí.
            </Typography>

            <Button
              onClick={() => {
                handleClose();
                navigate("/tienda");
              }}
              sx={{
                mt: 3,
                minWidth: 210,
                height: 46,
                borderRadius: "14px",
                textTransform: "none",
                fontWeight: 800,
                bgcolor: "#111111",
                color: "#ffffff",
                "&:hover": {
                  bgcolor: "#000000",
                },
              }}
            >
              Ir a la tienda
            </Button>
          </Box>
        ) : (
          <>
            <Box
              sx={{
                flex: 1,
                overflowY: "auto",
                px: 2,
                py: 2,
              }}
            >
              {cartItems.map((item) => {
                const codeText = [item.code, item.sku].filter(Boolean).join(
                  item.code && item.sku ? "-" : ""
                );
                const subtotal = Number(item.price || 0) * Number(item.quantity || 0);

                return (
                  <Box
                    key={item.key}
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "84px 1fr auto",
                      gap: 1.5,
                      alignItems: "center",
                      py: 1.5,
                    }}
                  >
                    <Box
                      sx={{
                        width: 84,
                        height: 84,
                        borderRadius: "16px",
                        bgcolor: "#f5f5f5",
                        overflow: "hidden",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Box
                        component="img"
                        src={item.image}
                        alt={item.name}
                        sx={{
                          width: "100%",
                          height: "100%",
                          objectFit: "contain",
                        }}
                      />
                    </Box>

                    <Box sx={{ minWidth: 0 }}>
                      <Typography
                        sx={{
                          fontSize: 14.5,
                          fontWeight: 800,
                          color: "#111111",
                          lineHeight: 1.2,
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                        }}
                      >
                        {item.name}
                      </Typography>

                      {!!item.variantLabel && (
                        <Typography
                          sx={{
                            mt: 0.6,
                            fontSize: 12.5,
                            color: "rgba(17,17,17,0.68)",
                          }}
                        >
                          {item.variantLabel}
                        </Typography>
                      )}

                      {!!codeText && (
                        <Typography
                          sx={{
                            mt: 0.45,
                            fontSize: 11.8,
                            color: "rgba(17,17,17,0.5)",
                          }}
                        >
                          Código: {codeText}
                        </Typography>
                      )}

                      <Box
                        sx={{
                          mt: 0.9,
                          display: "flex",
                          alignItems: "center",
                          gap: 0.8,
                          flexWrap: "wrap",
                        }}
                      >
                        {item.originalPrice && item.originalPrice > item.price ? (
                          <>
                            <Typography
                              sx={{
                                fontSize: 12.5,
                                color: "rgba(17,17,17,0.42)",
                                textDecoration: "line-through",
                              }}
                            >
                              ${Number(item.originalPrice).toFixed(2)}
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: 15.5,
                                fontWeight: 900,
                                color: "#d32f2f",
                              }}
                            >
                              ${Number(item.price).toFixed(2)}
                            </Typography>
                          </>
                        ) : (
                          <Typography
                            sx={{
                              fontSize: 15.5,
                              fontWeight: 900,
                              color: "#111111",
                            }}
                          >
                            ${Number(item.price).toFixed(2)}
                          </Typography>
                        )}
                      </Box>

                      <Box
                        sx={{
                          mt: 1,
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          flexWrap: "wrap",
                        }}
                      >
                        <Box
                          sx={{
                            height: 36,
                            borderRadius: "12px",
                            border: "1px solid rgba(17,17,17,0.12)",
                            overflow: "hidden",
                            display: "grid",
                            gridTemplateColumns: "36px 44px 36px",
                            backgroundColor: "#ffffff",
                          }}
                        >
                          <IconButton
                            onClick={() => decreaseCartItemQuantity(item.key)}
                            sx={{
                              borderRadius: 0,
                              color: "#4b5563",
                              height: "100%",
                            }}
                          >
                            <RemoveRoundedIcon sx={{ fontSize: 18 }} />
                          </IconButton>

                          <Box
                            sx={{
                              borderLeft: "1px solid rgba(17,17,17,0.08)",
                              borderRight: "1px solid rgba(17,17,17,0.08)",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <Typography
                              sx={{
                                fontSize: 14,
                                fontWeight: 800,
                                color: "#111111",
                              }}
                            >
                              {item.quantity}
                            </Typography>
                          </Box>

                          <IconButton
                            onClick={() => increaseCartItemQuantity(item.key)}
                            sx={{
                              borderRadius: 0,
                              color: "#4b5563",
                              height: "100%",
                            }}
                          >
                            <AddRoundedIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Box>

                        <Typography
                          sx={{
                            fontSize: 13.4,
                            fontWeight: 800,
                            color: "#111111",
                          }}
                        >
                          Subtotal: ${subtotal.toFixed(2)}
                        </Typography>
                      </Box>
                    </Box>

                    <IconButton
                      onClick={() => removeFromCart(item.key)}
                      aria-label="Eliminar producto del carrito"
                      sx={{
                        alignSelf: "flex-start",
                        width: 38,
                        height: 38,
                        borderRadius: 3,
                        color: "#d32f2f",
                        border: "1px solid rgba(211,47,47,0.16)",
                        bgcolor: "rgba(211,47,47,0.04)",
                        "&:hover": {
                          bgcolor: "rgba(211,47,47,0.08)",
                        },
                      }}
                    >
                      <DeleteOutlineRoundedIcon />
                    </IconButton>
                  </Box>
                );
              })}
            </Box>

            <Divider sx={{ borderColor: "rgba(17,17,17,0.08)" }} />

            <Box
              sx={{
                px: 2,
                py: 2,
              }}
            >
              <Box
                sx={{
                  mb: 1.5,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 2,
                }}
              >
                <Typography
                  sx={{
                    fontSize: 15,
                    color: "rgba(17,17,17,0.68)",
                  }}
                >
                  Total
                </Typography>

                <Typography
                  sx={{
                    fontSize: 24,
                    fontWeight: 700,
                    color: "#111111",
                    letterSpacing: "-0.03em",
                  }}
                >
                  ${total.toFixed(2)}
                </Typography>
              </Box>

              <Button
                fullWidth
                onClick={handleGoCheckout}
                sx={{
                  height: 50,
                  borderRadius: "16px",
                  textTransform: "none",
                  fontWeight: 800,
                  fontSize: 15,
                  bgcolor: "#111111",
                  color: "#ffffff",
                  "&:hover": {
                    bgcolor: "#000000",
                  },
                }}
              >
                Finalizar compra
              </Button>
            </Box>
          </>
        )}
      </Box>
    </Drawer>
  );
}